import { Badge, Button, Group, Stack, Text } from '@mantine/core';

import { useAuthOptional } from '@/contexts/AuthContext';
import { useAnalystRoster } from '@/hooks/useAnalystRoster';
import {
  canReplyToRegistryMessage,
  type RegistryOrder,
  type RegistryOrderMessage,
} from '@/types/registry-documents';
import { CountryMentionBadge } from './CountryMentionBadge';
import classes from './RegistryOrderMessages.module.css';

/**
 * When an email arrived, as a short local date and time: "01 Oct 2026,
 * 14:41". Local rather than UTC, because it is the moment something was
 * received, not a calendar day. Null for a missing or unreadable value.
 */
export function formatRegistryMessageTime(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** What the list needs to know of the order: its status decides which
 * messages can be answered, and its country is what a message that names
 * another country is set against. */
type OrderFacts = Pick<RegistryOrder, 'status'> & Partial<Pick<RegistryOrder, 'countryCode'>>;

interface RegistryOrderMessageListProps {
  messages: RegistryOrderMessage[];
  /** The order the messages are on. With `onReply`, decides which of
   * them can be answered. */
  order?: OrderFacts;
  /** Open the reply dialog for one of the desk's messages. Omitted where
   * replying is not offered. */
  onReply?: (message: RegistryOrderMessage) => void;
}

/**
 * The emails recorded on one order, oldest first, as data in and markup
 * out: the desk's messages and the replies sent to them from here.
 *
 * An email that brought documents is one line: the files themselves are
 * listed above. An email that brought none (a quote, a question, a
 * cancellation confirmed) is the part someone has to read, so its text is
 * shown in full, with a way to answer it. A reply sent from the order is
 * set apart as this side's own words.
 *
 * The text and the sender are chosen by whoever wrote the email. They are
 * rendered as text nodes only: never as markup, and a link in the text is
 * never made clickable.
 */
export function RegistryOrderMessageList({
  messages,
  order,
  onReply,
}: Readonly<RegistryOrderMessageListProps>) {
  if (messages.length === 0) {
    return null;
  }
  return (
    <Stack gap={6} data-testid="registry-order-messages">
      <Text size="xs" fw={600}>
        Messages from the registry desk
      </Text>
      <Stack gap={6} role="list" aria-label="Messages from the registry desk">
        {messages.map((message, index) =>
          // Emails carry no id of their own; the list is append-only and
          // oldest first, so a position names the same email on every read.
          message.direction === 'outbound' ? (
            <RegistryOutboundRow key={`${message.receivedAt ?? ''}:${index}`} message={message} />
          ) : (
            <RegistryMessageRow
              key={`${message.receivedAt ?? ''}:${index}`}
              message={message}
              orderCountryCode={order?.countryCode ?? null}
              onReply={replyOffered(order, message, onReply) ? onReply : undefined}
            />
          )
        )}
      </Stack>
    </Stack>
  );
}

/** Whether this message gets a Reply button: the surface offers replying,
 * and the API would accept one to this message. */
function replyOffered(
  order: OrderFacts | undefined,
  message: RegistryOrderMessage,
  onReply: ((message: RegistryOrderMessage) => void) | undefined
): boolean {
  if (order === undefined || onReply === undefined) {
    return false;
  }
  return canReplyToRegistryMessage(order, message);
}

/** The order a message is on, where one list carries several orders' mail:
 * its GTI reference, the name the research desk knows it by. */
export function OrderLabel({ label }: Readonly<{ label: string | undefined }>) {
  if (!label) {
    return null;
  }
  return (
    <Badge size="xs" variant="outline" color="gray" ff="monospace" tt="none">
      {label}
    </Badge>
  );
}

/** What an email from the desk is flagged with: a sender the strict rules
 * refused, an answer already sent, a country named in the text, and how
 * the email found its order when not by its reference. Shared by the
 * order's message list and the report's Armadillo conversation. */
export function RegistryMessageBadges({
  message,
  orderCountryCode,
}: Readonly<{
  message: RegistryOrderMessage;
  /** The order's country, for the hover of a message naming another. */
  orderCountryCode: string | null;
}>) {
  return (
    <>
      {!message.senderVerified && (
        <Badge size="xs" variant="light" color="orange">
          Sender not verified
        </Badge>
      )}
      {message.answered && (
        <Badge size="xs" variant="light" color="green">
          Answered
        </Badge>
      )}
      <CountryMentionBadge codes={message.countriesMentioned} orderCountryCode={orderCountryCode} />
      {/* How the email found the order, when it was not by its reference:
          a thread the worker followed, or a person's hand. */}
      {message.matchedBy === 'thread' && (
        <Badge size="xs" variant="light" color="gray">
          Matched by email thread
        </Badge>
      )}
      {message.matchedBy === 'manual' && (
        <Badge size="xs" variant="light" color="gray" data-testid="registry-message-attached-by">
          Attached by <ReplyAuthor userId={message.attachedByUserId} />
        </Badge>
      )}
    </>
  );
}

/** What to do about an unverified sender. Written out rather than left to
 * a tooltip on the badge: it has to reach a keyboard and a touch screen. */
export function UnverifiedSenderNote() {
  return (
    <Text size="xs" c="dimmed">
      The sender did not pass the strict checks, so any attachments were withheld. Confirm in the
      mailbox before acting on this message.
    </Text>
  );
}

/** What a cut email says under its text. */
export const TRUNCATED_NOTE = 'This message is longer than shown - read it in the mailbox';

/**
 * One email from the research desk: when it came and from whom, what it
 * brought, and its text when it brought no document. Exported for the
 * report's Armadillo conversation, which lists several orders' mail in one
 * column and labels each email with its order (`orderLabel`).
 */
export function RegistryMessageRow({
  message,
  orderCountryCode,
  onReply,
  orderLabel,
}: Readonly<{
  message: RegistryOrderMessage;
  /** The order's country, for the hover of a message naming another. */
  orderCountryCode: string | null;
  onReply?: (message: RegistryOrderMessage) => void;
  /** The order's reference, where the list holds more than one order. */
  orderLabel?: string;
}>) {
  const delivery = message.newDocuments > 0;
  const facts = [formatRegistryMessageTime(message.receivedAt), message.sender?.trim()]
    .filter(Boolean)
    .join(' · ');

  return (
    <Stack gap={4} role="listitem" className={delivery ? classes.delivery : classes.reply}>
      <Group gap={6} wrap="wrap">
        <OrderLabel label={orderLabel} />
        <Text size="xs" c="dimmed" className={classes.facts}>
          {facts || 'Date and sender not recorded'}
        </Text>
        {delivery && (
          <Text size="xs">
            Delivered {message.newDocuments} {message.newDocuments === 1 ? 'file' : 'files'}
          </Text>
        )}
        <RegistryMessageBadges message={message} orderCountryCode={orderCountryCode} />
        {onReply && (
          <Button
            size="compact-xs"
            variant="light"
            ml="auto"
            onClick={() => onReply(message)}
            aria-label={`Reply to the message${facts ? ` of ${facts}` : ''}`}
          >
            Reply
          </Button>
        )}
      </Group>
      {!message.senderVerified && <UnverifiedSenderNote />}
      {!delivery && <ReplyText message={message} />}
    </Stack>
  );
}

/** A reply sent from the order: who sent it and when, then its text. It
 * is this side's own, so it is never marked as an unverified sender.
 * Exported, with `orderLabel`, for the report's Armadillo conversation. */
export function RegistryOutboundRow({
  message,
  orderLabel,
}: Readonly<{ message: RegistryOrderMessage; orderLabel?: string }>) {
  const when = formatRegistryMessageTime(message.receivedAt);
  return (
    <Stack gap={4} role="listitem" className={classes.outbound} data-direction="outbound">
      <Group gap={6} wrap="wrap">
        <OrderLabel label={orderLabel} />
        <Text size="xs" c="dimmed" className={classes.facts}>
          <ReplyAuthor userId={message.sentByUserId} /> replied{when ? ` · ${when}` : ''}
        </Text>
      </Group>
      <ReplyText message={message} />
    </Stack>
  );
}

/** "You" for the signed-in person's own reply, else the colleague's name.
 * The auth user id is typed as a string but arrives from Django as an
 * integer; the message records it as a string. Compared as strings, as
 * the other surfaces that label the viewer's own items do. */
function ReplyAuthor({ userId }: Readonly<{ userId: string | null }>) {
  const auth = useAuthOptional();
  const currentUserId = auth?.user?.id != null ? String(auth.user.id) : null;
  if (userId !== null && userId === currentUserId) {
    return <>You</>;
  }
  return <ColleagueName userId={userId} />;
}

/** The colleague's name from the administrator roster, read only once a
 * reply that is not the viewer's own is on screen. */
function ColleagueName({ userId }: Readonly<{ userId: string | null }>) {
  const members = useAnalystRoster();
  const member = userId === null ? undefined : members.find((m) => String(m.id) === userId);
  return <>{member?.name ?? 'A colleague'}</>;
}

function ReplyText({ message }: Readonly<{ message: RegistryOrderMessage }>) {
  if (message.text.trim() === '') {
    return (
      <Text size="xs" c="dimmed">
        No text was kept for this message - read it in the mailbox.
      </Text>
    );
  }
  return (
    <>
      <Text size="sm" className={classes.text}>
        {message.text}
      </Text>
      {message.truncated && (
        <Text size="xs" c="dimmed">
          {TRUNCATED_NOTE}
        </Text>
      )}
    </>
  );
}
